import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Badge,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Tooltip,
  LinearProgress,
  InputAdornment,
  Avatar,
  Switch,
  FormControlLabel,
  Menu,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  Dashboard as DashboardIcon,
  GridView as RackSheetIcon,
  FlightLand as ArrivalIcon,
  Map as MapIcon,
  BookmarkBorder as TemplateIcon,
  Schedule as ScheduledIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  FileDownload as DownloadIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Save as SaveIcon,
  Settings as SettingsIcon,
  DateRange as DateRangeIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as ChartIcon,
  BarChart as BarChartIcon,
  TableChart as TableIcon,
  ViewList as ListViewIcon,
  ViewModule as GridViewIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Layers as LayersIcon,
  Info as InfoIcon,
  CheckCircle as OccupiedIcon,
  RadioButtonUnchecked as VacantIcon,
  Build as MaintenanceIcon,
  Block as OutOfOrderIcon,
  MonetizationOn as RevenueIcon,
  ColorLens as ColorIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  PlayArrow as GenerateIcon,
  ContentCopy as CopyIcon,
  Star as StarIcon,
  FolderOpen as FolderIcon,
  CloudDownload as CloudDownloadIcon,
  Email as EmailIcon,
  InsertChart as InsertChartIcon,
  GridOn as GridOnIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  Phone,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { styled, alpha } from '@mui/material/styles';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';
import ScheduledReportsManagement from '../components/reports/ScheduledReportsManagement';
import ReportBuilder from '../components/reports/ReportBuilder';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  '& .MuiCardContent-root': {
    padding: theme.spacing(3),
  },
}));

const SiteStatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => {
  const getColor = () => {
    switch (status) {
      case 'occupied': return { bg: '#4caf50', color: '#fff' };
      case 'vacant': return { bg: '#e0e0e0', color: '#000' };
      case 'maintenance': return { bg: '#ff9800', color: '#fff' };
      case 'out_of_order': return { bg: '#f44336', color: '#fff' };
      case 'reserved': return { bg: '#2196f3', color: '#fff' };
      default: return { bg: '#9e9e9e', color: '#fff' };
    }
  };

  const colors = getColor();
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    minWidth: 80,
  };
});

const MapOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(8px)',
  borderRadius: theme.spacing(1),
  padding: theme.spacing(2),
  zIndex: 1000,
}));

// Types
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ReportFilter {
  dateRange: {
    start: Dayjs | null;
    end: Dayjs | null;
  };
  siteTypes: string[];
  sections: string[];
  status: string;
  search: string;
}

interface Site {
  id: string;
  number: string;
  type: string;
  section: string;
  status: 'occupied' | 'vacant' | 'maintenance' | 'out_of_order' | 'reserved';
  guest?: {
    name: string;
    checkIn: Date;
    checkOut: Date;
    phone: string;
    email: string;
  };
  revenue?: number;
  lastMaintenance?: Date;
  coordinates?: { x: number; y: number };
}

interface ArrivalRecord {
  id: string;
  guestName: string;
  siteNumber: string;
  checkIn: Date;
  checkOut: Date;
  type: 'arrival' | 'departure';
  status: 'confirmed' | 'pending' | 'cancelled';
  phone: string;
  email: string;
  notes?: string;
  partySize: number;
  revenue: number;
}

interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  type: 'rack_sheet' | 'arrival' | 'map' | 'custom';
  configuration: any;
  isPublic: boolean;
  createdBy: string;
  createdAt: Date;
  tags: string[];
  rating: number;
  usageCount: number;
}

// Tab Panel Component
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`reports-tabpanel-${index}`}
      aria-labelledby={`reports-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export default function ReportsPage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30); // seconds
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [mapView, setMapView] = useState<'occupancy' | 'revenue' | 'maintenance'>('occupancy');
  const [fullscreen, setFullscreen] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  
  // Filters
  const [filters, setFilters] = useState<ReportFilter>({
    dateRange: {
      start: dayjs().subtract(7, 'days'),
      end: dayjs()
    },
    siteTypes: [],
    sections: [],
    status: 'all',
    search: ''
  });

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Dashboard Stats
  const [dashboardStats, setDashboardStats] = useState({
    totalSites: 0,
    occupiedSites: 0,
    occupancyRate: 0,
    todayArrivals: 0,
    todayDepartures: 0,
    totalRevenue: 0,
    averageStayLength: 0,
    maintenanceAlerts: 0
  });

  const [sites, setSites] = useState<Site[]>([]);
  const [arrivals, setArrivals] = useState<ArrivalRecord[]>([]);
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  
  // Custom Report Builder State
  const [reportBuilderOpen, setReportBuilderOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any>(null);
  const [customReports, setCustomReports] = useState<any[]>([]);

  // Auto-refresh effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoRefresh && refreshInterval > 0) {
      interval = setInterval(() => {
        fetchReportData();
      }, refreshInterval * 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, refreshInterval, currentTab]);

  // Initial data load
  useEffect(() => {
    fetchReportData();
  }, [currentTab, filters, page, rowsPerPage]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      switch (currentTab) {
        case 0:
          await fetchDashboardData();
          break;
        case 1:
          await fetchRackSheetData();
          break;
        case 2:
          await fetchArrivalData();
          break;
        case 3:
          await fetchMapData();
          break;
        case 4:
          await fetchTemplateData();
          break;
        case 5:
          // Scheduled reports data is handled by the ScheduledReportsManagement component
          break;
        case 6:
          await loadCustomReports();
          break;
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/reports/dashboard');
      if (response.data.success) {
        const data = response.data.data;
        setDashboardStats({
          totalSites: data.totalSites || 0,
          occupiedSites: data.occupiedSites || 0,
          occupancyRate: parseFloat(data.occupancyRate) || 0,
          todayArrivals: data.todayArrivals || data.todayCheckIns || 0,
          todayDepartures: data.todayDepartures || data.todayCheckOuts || 0,
          totalRevenue: data.totalRevenue || 0,
          averageStayLength: 4.2, // This would need to be calculated separately
          maintenanceAlerts: 3 // This would need to come from maintenance API
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // Fallback to default values on error
      setDashboardStats({
        totalSites: 0,
        occupiedSites: 0,
        occupancyRate: 0,
        todayArrivals: 0,
        todayDepartures: 0,
        totalRevenue: 0,
        averageStayLength: 0,
        maintenanceAlerts: 0
      });
    }
  };

  const fetchRackSheetData = async () => {
    try {
      const response = await api.get('/reports/rack-sheet', {
        params: {
          locationId: selectedLocation,
          date: selectedDate.toISOString()
        }
      });
      setSites(response.data.sites || []);
    } catch (error) {
      console.error('Error fetching rack sheet data:', error);
      // Fallback to empty array if API fails
      setSites([]);
    }
  };

  const fetchArrivalData = async () => {
    try {
      const response = await api.get('/reports/arrivals', {
        params: {
          locationId: selectedLocation,
          date: selectedDate.toISOString()
        }
      });
      setArrivals(response.data.arrivals || []);
    } catch (error) {
      console.error('Error fetching arrival data:', error);
      // Fallback to empty array if API fails
      setArrivals([]);
    }
  };

  const fetchMapData = async () => {
    // Reuse site data for map
    await fetchRackSheetData();
  };

  const fetchTemplateData = async () => {
    try {
      const response = await api.get('/reports/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      // Fallback to empty array if API fails
      setTemplates([]);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleFilterChange = (field: keyof ReportFilter, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleExportReport = (format: 'pdf' | 'excel' | 'csv') => {
    console.log(`Exporting report as ${format}`);
    setExportMenuAnchor(null);
    // Implement export logic here
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleSiteClick = (site: Site) => {
    setSelectedSite(site);
    setSiteDialogOpen(true);
  };

  const handleTemplateClick = (template: ReportTemplate) => {
    setSelectedTemplate(template);
    setTemplateDialogOpen(true);
  };

  const handleGenerateFromTemplate = (template: ReportTemplate) => {
    console.log('Generating report from template:', template.name);
    // Implement template generation logic
  };

  // Custom Report Builder Functions
  const loadCustomReports = async () => {
    try {
      const response = await api.get('/report-builder');
      setCustomReports(response.data.reports || []);
    } catch (error) {
      console.error('Error loading custom reports:', error);
      setCustomReports([]);
    }
  };

  const handleCreateCustomReport = () => {
    setSelectedReport(null);
    setReportBuilderOpen(true);
  };

  const handleEditCustomReport = (report: any) => {
    setSelectedReport(report);
    setReportBuilderOpen(true);
  };

  const handleSaveCustomReport = async (reportData: any) => {
    try {
      if (selectedReport) {
        // Update existing report
        await api.put(`/report-builder/${selectedReport.reportId}`, reportData);
      } else {
        // Create new report
        await api.post('/report-builder', reportData);
      }
      await loadCustomReports();
    } catch (error) {
      console.error('Error saving custom report:', error);
    }
  };

  const handleDeleteCustomReport = async (reportId: string) => {
    try {
      await api.delete(`/report-builder/${reportId}`);
      await loadCustomReports();
    } catch (error) {
      console.error('Error deleting custom report:', error);
    }
  };

  const handleExecuteCustomReport = async (reportId: string, format: string = 'json') => {
    try {
      const response = await api.post(`/report-builder/${reportId}/execute`, {
        filters: {},
        format: format
      });
      
      if (format === 'json') {
        console.log('Report data:', response.data);
        // Handle JSON response - could open preview dialog
      } else {
        // Handle file download for PDF, Excel, CSV
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${reportId}_report.${format}`;
        link.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error executing custom report:', error);
    }
  };

  const renderDashboard = () => (
    <Grid container spacing={3}>
      {/* Key Metrics */}
      <Grid item xs={12} sm={6} md={3}>
        <StatCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                <HomeIcon />
              </Avatar>
              <Typography variant="h6">Total Sites</Typography>
            </Box>
            <Typography variant="h4">
              {dashboardStats.totalSites}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Available sites
            </Typography>
          </CardContent>
        </StatCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                <OccupiedIcon />
              </Avatar>
              <Typography variant="h6">Occupied</Typography>
            </Box>
            <Typography variant="h4">
              {dashboardStats.occupiedSites}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {dashboardStats.occupancyRate}% occupancy
            </Typography>
          </CardContent>
        </StatCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                <ArrivalIcon />
              </Avatar>
              <Typography variant="h6">Today's Arrivals</Typography>
            </Box>
            <Typography variant="h4">
              {dashboardStats.todayArrivals}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {dashboardStats.todayDepartures} departures
            </Typography>
          </CardContent>
        </StatCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StatCard>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                <RevenueIcon />
              </Avatar>
              <Typography variant="h6">Total Revenue</Typography>
            </Box>
            <Typography variant="h4">
              ${dashboardStats.totalRevenue.toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              This month
            </Typography>
          </CardContent>
        </StatCard>
      </Grid>

      {/* Quick Actions */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Quick Actions
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            <Button
              variant="contained"
              startIcon={<RackSheetIcon />}
              onClick={() => setCurrentTab(1)}
            >
              View Rack Sheet
            </Button>
            <Button
              variant="contained"
              startIcon={<ArrivalIcon />}
              onClick={() => setCurrentTab(2)}
            >
              Arrival Report
            </Button>
            <Button
              variant="contained"
              startIcon={<MapIcon />}
              onClick={() => setCurrentTab(3)}
            >
              Site Map
            </Button>
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={(e) => setExportMenuAnchor(e.currentTarget)}
            >
              Export Report
            </Button>
          </Stack>
        </Paper>
      </Grid>

      {/* Recent Activity */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Activity
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
                  <ArrivalIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary="New arrival - Site 45"
                secondary="John Smith checked in • 2 hours ago"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                  <MaintenanceIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary="Maintenance alert - Site 12"
                secondary="Electrical issue reported • 4 hours ago"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                  <ExportIcon fontSize="small" />
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary="Report generated"
                secondary="Weekly occupancy report • 6 hours ago"
              />
            </ListItem>
          </List>
        </Paper>
      </Grid>

      {/* Alerts */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Alerts & Notifications
          </Typography>
          <Stack spacing={2}>
            <Alert severity="warning" action={
              <Button color="inherit" size="small">
                View
              </Button>
            }>
              {dashboardStats.maintenanceAlerts} sites require maintenance attention
            </Alert>
            <Alert severity="info" action={
              <Button color="inherit" size="small">
                Review
              </Button>
            }>
              5 reservations pending confirmation
            </Alert>
            <Alert severity="success">
              Occupancy rate is above target (84.7% vs 80%)
            </Alert>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderRackSheet = () => (
    <Box>
      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search sites..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="occupied">Occupied</MenuItem>
                <MenuItem value="vacant">Vacant</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="reserved">Reserved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <DatePicker
              label="Start Date"
              value={filters.dateRange.start}
              onChange={(date) => handleFilterChange('dateRange', { ...filters.dateRange, start: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <DatePicker
              label="End Date"
              value={filters.dateRange.end}
              onChange={(date) => handleFilterChange('dateRange', { ...filters.dateRange, end: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <Stack direction="row" spacing={1}>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, value) => value && setViewMode(value)}
                size="small"
              >
                <ToggleButton value="grid">
                  <GridViewIcon />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListViewIcon />
                </ToggleButton>
              </ToggleButtonGroup>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchReportData}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              >
                Export
              </Button>
            </Stack>
          </Grid>
        </Grid>

        {/* Auto-refresh controls */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
            }
            label="Auto-refresh"
          />
          {autoRefresh && (
            <TextField
              type="number"
              label="Interval (seconds)"
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(parseInt(e.target.value))}
              sx={{ width: 150 }}
              inputProps={{ min: 10, max: 300 }}
            />
          )}
        </Box>
      </Paper>

      {/* Site Display */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={2}>
          {sites.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((site) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={site.id}>
              <StyledCard onClick={() => handleSiteClick(site)}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Site {site.number}</Typography>
                    <SiteStatusChip size="small" status={site.status} label={site.status} />
                  </Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {site.type} • Section {site.section}
                  </Typography>
                  {site.guest && (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {site.guest.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {dayjs(site.guest.checkIn).format('MMM DD')} - {dayjs(site.guest.checkOut).format('MMM DD')}
                      </Typography>
                    </Box>
                  )}
                  {site.revenue && (
                    <Typography variant="body2" sx={{ mt: 1, color: 'success.main' }}>
                      Revenue: ${site.revenue.toFixed(2)}
                    </Typography>
                  )}
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Site</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Section</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Guest</TableCell>
                <TableCell>Check-in</TableCell>
                <TableCell>Check-out</TableCell>
                <TableCell>Revenue</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sites.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((site) => (
                <TableRow key={site.id} hover onClick={() => handleSiteClick(site)} sx={{ cursor: 'pointer' }}>
                  <TableCell>{site.number}</TableCell>
                  <TableCell>{site.type}</TableCell>
                  <TableCell>{site.section}</TableCell>
                  <TableCell>
                    <SiteStatusChip size="small" status={site.status} label={site.status} />
                  </TableCell>
                  <TableCell>{site.guest?.name || '-'}</TableCell>
                  <TableCell>
                    {site.guest ? dayjs(site.guest.checkIn).format('MMM DD, YYYY') : '-'}
                  </TableCell>
                  <TableCell>
                    {site.guest ? dayjs(site.guest.checkOut).format('MMM DD, YYYY') : '-'}
                  </TableCell>
                  <TableCell>${site.revenue?.toFixed(2) || '0.00'}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={(e) => {
                      e.stopPropagation();
                      handleSiteClick(site);
                    }}>
                      <ViewIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      <TablePagination
        component="div"
        count={sites.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[12, 24, 48, 96]}
      />
    </Box>
  );

  const renderArrivalReport = () => (
    <Box>
      {/* Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              placeholder="Search guests..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <DatePicker
              label="Start Date"
              value={filters.dateRange.start}
              onChange={(date) => handleFilterChange('dateRange', { ...filters.dateRange, start: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <DatePicker
              label="End Date"
              value={filters.dateRange.end}
              onChange={(date) => handleFilterChange('dateRange', { ...filters.dateRange, end: date })}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchReportData}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExportIcon />}
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
              >
                Export
              </Button>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrintReport}>
                Print
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Summary Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" color="primary">
                Total Arrivals
              </Typography>
              <Typography variant="h4">
                {arrivals.filter(a => a.type === 'arrival').length}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" color="secondary">
                Total Departures
              </Typography>
              <Typography variant="h4">
                {arrivals.filter(a => a.type === 'departure').length}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" color="success.main">
                Confirmed
              </Typography>
              <Typography variant="h4">
                {arrivals.filter(a => a.status === 'confirmed').length}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledCard>
            <CardContent>
              <Typography variant="h6" color="info.main">
                Revenue
              </Typography>
              <Typography variant="h4">
                ${arrivals.reduce((sum, a) => sum + a.revenue, 0).toLocaleString()}
              </Typography>
            </CardContent>
          </StyledCard>
        </Grid>
      </Grid>

      {/* Arrivals Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Guest Name</TableCell>
              <TableCell>Site</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Check-in</TableCell>
              <TableCell>Check-out</TableCell>
              <TableCell>Party Size</TableCell>
              <TableCell>Revenue</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Contact</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            ) : (
              arrivals.slice(page * rowsPerPage, (page + 1) * rowsPerPage).map((arrival) => (
                <TableRow key={arrival.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        <PersonIcon />
                      </Avatar>
                      {arrival.guestName}
                    </Box>
                  </TableCell>
                  <TableCell>Site {arrival.siteNumber}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={arrival.type}
                      color={arrival.type === 'arrival' ? 'success' : 'secondary'}
                      icon={<ArrivalIcon />}
                    />
                  </TableCell>
                  <TableCell>{dayjs(arrival.checkIn).format('MMM DD, YYYY HH:mm')}</TableCell>
                  <TableCell>{dayjs(arrival.checkOut).format('MMM DD, YYYY HH:mm')}</TableCell>
                  <TableCell>{arrival.partySize}</TableCell>
                  <TableCell>${arrival.revenue.toFixed(2)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={arrival.status}
                      color={
                        arrival.status === 'confirmed' ? 'success' :
                        arrival.status === 'pending' ? 'warning' : 'error'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title={arrival.phone}>
                        <IconButton size="small">
                          <Phone />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={arrival.email}>
                        <IconButton size="small">
                          <EmailIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={arrivals.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Box>
  );

  const renderMapReport = () => (
    <Box sx={{ position: 'relative', height: 600 }}>
      {/* Map Controls */}
      <MapOverlay>
        <Stack spacing={2}>
          <Typography variant="h6">Map Controls</Typography>
          <FormControl fullWidth>
            <InputLabel>Overlay Type</InputLabel>
            <Select
              value={mapView}
              onChange={(e) => setMapView(e.target.value as any)}
              label="Overlay Type"
            >
              <MenuItem value="occupancy">Occupancy Status</MenuItem>
              <MenuItem value="revenue">Revenue</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => setFullscreen(!fullscreen)}>
              {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
            <IconButton>
              <ZoomInIcon />
            </IconButton>
            <IconButton>
              <ZoomOutIcon />
            </IconButton>
            <IconButton>
              <LayersIcon />
            </IconButton>
          </Stack>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => handleExportReport('pdf')}
          >
            Export Map
          </Button>
        </Stack>
      </MapOverlay>

      {/* Map Canvas */}
      <Paper
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
          bgcolor: '#f5f5f5'
        }}
      >
        <svg width="100%" height="100%" viewBox="0 0 800 600">
          {/* Background grid */}
          <defs>
            <pattern id="grid" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#e0e0e0" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
          
          {/* Sites */}
          {sites.map((site) => {
            const color = mapView === 'occupancy' ? 
              (site.status === 'occupied' ? '#4caf50' :
               site.status === 'vacant' ? '#e0e0e0' :
               site.status === 'maintenance' ? '#ff9800' :
               site.status === 'reserved' ? '#2196f3' : '#f44336') :
              mapView === 'revenue' ? 
              `hsl(${Math.min(site.revenue || 0, 200) / 200 * 120}, 70%, 50%)` :
              '#9e9e9e';

            return (
              <g key={site.id}>
                <rect
                  x={site.coordinates?.x || 0}
                  y={site.coordinates?.y || 0}
                  width="30"
                  height="30"
                  fill={color}
                  stroke="#333"
                  strokeWidth="1"
                  rx="4"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSiteClick(site)}
                />
                <text
                  x={(site.coordinates?.x || 0) + 15}
                  y={(site.coordinates?.y || 0) + 20}
                  textAnchor="middle"
                  fontSize="10"
                  fill="#333"
                  style={{ pointerEvents: 'none' }}
                >
                  {site.number}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Legend */}
        <Box sx={{ position: 'absolute', bottom: 16, left: 16, bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>Legend</Typography>
          <Stack spacing={1}>
            {mapView === 'occupancy' && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#4caf50', borderRadius: 1 }} />
                  <Typography variant="body2">Occupied</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#e0e0e0', borderRadius: 1 }} />
                  <Typography variant="body2">Vacant</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#ff9800', borderRadius: 1 }} />
                  <Typography variant="body2">Maintenance</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 16, height: 16, bgcolor: '#2196f3', borderRadius: 1 }} />
                  <Typography variant="body2">Reserved</Typography>
                </Box>
              </>
            )}
            {mapView === 'revenue' && (
              <Typography variant="body2">Color intensity indicates revenue level</Typography>
            )}
          </Stack>
        </Box>
      </Paper>
    </Box>
  );

  const renderTemplates = () => (
    <Box>
      {/* Template Actions */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6">Report Templates</Typography>
          <Button variant="contained" startIcon={<AddIcon />}>
            Create Template
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchTemplateData}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      {/* Template Grid */}
      <Grid container spacing={3}>
        {templates.map((template) => (
          <Grid item xs={12} md={6} lg={4} key={template.id}>
            <StyledCard onClick={() => handleTemplateClick(template)}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    <TemplateIcon />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="h6">{template.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {template.type.replace('_', ' ')}
                    </Typography>
                  </Box>
                  {template.isPublic && (
                    <Chip size="small" label="Public" color="success" />
                  )}
                </Box>
                <Typography variant="body2" paragraph>
                  {template.description}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StarIcon sx={{ color: '#ffc107', fontSize: 16 }} />
                    <Typography variant="body2">{template.rating}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Used {template.usageCount} times
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  {template.tags.map((tag) => (
                    <Chip key={tag} size="small" label={tag} variant="outlined" />
                  ))}
                </Stack>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  startIcon={<GenerateIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGenerateFromTemplate(template);
                  }}
                >
                  Generate
                </Button>
                <Button size="small" startIcon={<CopyIcon />}>
                  Copy
                </Button>
                <Button size="small" startIcon={<ShareIcon />}>
                  Share
                </Button>
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderCustomReportBuilder = () => (
    <Box>
      {/* Header and Actions */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Custom Report Builder</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateCustomReport}
          >
            Create New Report
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Build custom reports with drag-and-drop field selection, advanced filtering, and multiple output formats.
        </Typography>
      </Paper>

      {/* Custom Reports Grid */}
      <Grid container spacing={3}>
        {customReports.map((report) => (
          <Grid item xs={12} sm={6} md={4} key={report.reportId}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                    <InsertChartIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="h6" noWrap>
                      {report.name}
                    </Typography>
                    <Chip 
                      size="small" 
                      label={report.category} 
                      color="primary" 
                      variant="outlined" 
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                  {report.description || 'No description provided'}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {report.fields?.length || 0} fields
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Used {report.usageStats?.totalRuns || 0} times
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(report.createdAt).toLocaleDateString()}
                  </Typography>
                  {report.usageStats?.lastRun && (
                    <Typography variant="caption" color="text.secondary">
                      Last run: {new Date(report.usageStats.lastRun).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
              <CardActions>
                <Button 
                  size="small" 
                  startIcon={<GenerateIcon />}
                  onClick={() => handleExecuteCustomReport(report.reportId, 'json')}
                >
                  Run
                </Button>
                <Button 
                  size="small" 
                  startIcon={<EditIcon />}
                  onClick={() => handleEditCustomReport(report)}
                >
                  Edit
                </Button>
                <Menu
                  anchorEl={null}
                  open={false}
                  onClose={() => {}}
                >
                  <MenuItem onClick={() => handleExecuteCustomReport(report.reportId, 'pdf')}>
                    <ListItemIcon><DownloadIcon /></ListItemIcon>
                    Export as PDF
                  </MenuItem>
                  <MenuItem onClick={() => handleExecuteCustomReport(report.reportId, 'excel')}>
                    <ListItemIcon><DownloadIcon /></ListItemIcon>
                    Export as Excel
                  </MenuItem>
                  <MenuItem onClick={() => handleExecuteCustomReport(report.reportId, 'csv')}>
                    <ListItemIcon><DownloadIcon /></ListItemIcon>
                    Export as CSV
                  </MenuItem>
                  <Divider />
                  <MenuItem 
                    onClick={() => handleDeleteCustomReport(report.reportId)}
                    sx={{ color: 'error.main' }}
                  >
                    <ListItemIcon><DeleteIcon sx={{ color: 'error.main' }} /></ListItemIcon>
                    Delete Report
                  </MenuItem>
                </Menu>
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
        
        {customReports.length === 0 && (
          <Grid item xs={12}>
            <Box 
              sx={{ 
                textAlign: 'center', 
                py: 8,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              <InsertChartIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Custom Reports Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first custom report with drag-and-drop field selection and advanced filtering.
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateCustomReport}
              >
                Create Your First Report
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Specialized Reporting System
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive reporting tools for occupancy, arrivals, and site management
        </Typography>
      </Box>

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<RackSheetIcon />} label="Rack Sheets" />
          <Tab icon={<ArrivalIcon />} label="Arrival Reports" />
          <Tab icon={<MapIcon />} label="Map Reports" />
          <Tab icon={<TemplateIcon />} label="Report Templates" />
          <Tab icon={<ScheduledIcon />} label="Scheduled Reports" />
          <Tab icon={<InsertChartIcon />} label="Custom Reports" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={currentTab} index={0}>
        {renderDashboard()}
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {renderRackSheet()}
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {renderArrivalReport()}
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {renderMapReport()}
      </TabPanel>

      <TabPanel value={currentTab} index={4}>
        {renderTemplates()}
      </TabPanel>

      <TabPanel value={currentTab} index={5}>
        <ScheduledReportsManagement />
      </TabPanel>

      <TabPanel value={currentTab} index={6}>
        {renderCustomReportBuilder()}
      </TabPanel>

      {/* Site Details Dialog */}
      <Dialog
        open={siteDialogOpen}
        onClose={() => setSiteDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Site {selectedSite?.number} Details
        </DialogTitle>
        <DialogContent>
          {selectedSite && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Site Information</Typography>
                <Typography><strong>Number:</strong> {selectedSite.number}</Typography>
                <Typography><strong>Type:</strong> {selectedSite.type}</Typography>
                <Typography><strong>Section:</strong> {selectedSite.section}</Typography>
                <Typography><strong>Status:</strong> {selectedSite.status}</Typography>
                {selectedSite.revenue && (
                  <Typography><strong>Revenue:</strong> ${selectedSite.revenue.toFixed(2)}</Typography>
                )}
              </Grid>
              {selectedSite.guest && (
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Guest Information</Typography>
                  <Typography><strong>Name:</strong> {selectedSite.guest.name}</Typography>
                  <Typography><strong>Check-in:</strong> {dayjs(selectedSite.guest.checkIn).format('MMM DD, YYYY')}</Typography>
                  <Typography><strong>Check-out:</strong> {dayjs(selectedSite.guest.checkOut).format('MMM DD, YYYY')}</Typography>
                  <Typography><strong>Phone:</strong> {selectedSite.guest.phone}</Typography>
                  <Typography><strong>Email:</strong> {selectedSite.guest.email}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSiteDialogOpen(false)}>Close</Button>
          <Button variant="contained" startIcon={<EditIcon />}>Edit</Button>
        </DialogActions>
      </Dialog>

      {/* Template Details Dialog */}
      <Dialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTemplate?.name}
        </DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Box>
              <Typography variant="body1" paragraph>
                {selectedTemplate.description}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Template Details</Typography>
                <Typography><strong>Type:</strong> {selectedTemplate.type.replace('_', ' ')}</Typography>
                <Typography><strong>Created by:</strong> {selectedTemplate.createdBy}</Typography>
                <Typography><strong>Created:</strong> {dayjs(selectedTemplate.createdAt).format('MMM DD, YYYY')}</Typography>
                <Typography><strong>Rating:</strong> {selectedTemplate.rating}/5</Typography>
                <Typography><strong>Usage:</strong> {selectedTemplate.usageCount} times</Typography>
              </Box>
              <Box>
                <Typography variant="h6" gutterBottom>Tags</Typography>
                <Stack direction="row" spacing={1}>
                  {selectedTemplate.tags.map((tag) => (
                    <Chip key={tag} size="small" label={tag} />
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>Close</Button>
          <Button startIcon={<CopyIcon />}>Copy</Button>
          <Button startIcon={<ShareIcon />}>Share</Button>
          <Button variant="contained" startIcon={<GenerateIcon />}>
            Generate Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleExportReport('pdf')}>
          <ListItemIcon>
            <DownloadIcon />
          </ListItemIcon>
          Export as PDF
        </MenuItem>
        <MenuItem onClick={() => handleExportReport('excel')}>
          <ListItemIcon>
            <DownloadIcon />
          </ListItemIcon>
          Export as Excel
        </MenuItem>
        <MenuItem onClick={() => handleExportReport('csv')}>
          <ListItemIcon>
            <DownloadIcon />
          </ListItemIcon>
          Export as CSV
        </MenuItem>
      </Menu>

      {/* Custom Report Builder Dialog */}
      <ReportBuilder
        open={reportBuilderOpen}
        onClose={() => setReportBuilderOpen(false)}
        report={selectedReport}
        onSave={handleSaveCustomReport}
      />
    </Container>
  );
}